// src/pages/proyectos/ProyectoDetallePage.tsx
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Badge, Spinner, Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui';
import { ArrowLeft, Edit2, Clock, Calendar, Building2, User, Layers, GitBranch, Users } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useProyecto, useCambiarEstadoProyecto } from '@/hooks/queries/useProyectos';
import { PROJECT_STATUS_LABELS, PROJECT_STATUS_COLORS } from '@/types';
import type { ProjectStatus } from '@/types';
import { ProyectoFormModal } from './components/ProyectoFormModal';
import { FasesList } from './components/FasesList';
import { SprintsList } from './components/SprintsList';
import { MiembrosList } from './components/MiembrosList';
import { ProyectoResumenCard } from './components/ProyectoResumenCard';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';

export function ProyectoDetallePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: proyecto, isLoading } = useProyecto(id!);
  const cambiarEstadoMutation = useCambiarEstadoProyecto();
  const [editModalOpen, setEditModalOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!proyecto) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Proyecto no encontrado</p>
        <Button variant="ghost" onClick={() => navigate('/proyectos')} className="mt-4">
          Volver a proyectos
        </Button>
      </div>
    );
  }

  const handleEstadoChange = async (estado: ProjectStatus) => {
    await cambiarEstadoMutation.mutateAsync({ id: proyecto.id, estado });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/proyectos')}
            className="mt-1"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{proyecto.nombre}</h1>
              <Badge className={PROJECT_STATUS_COLORS[proyecto.estado]}>
                {PROJECT_STATUS_LABELS[proyecto.estado]}
              </Badge>
            </div>
            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
              <span className="font-mono">{proyecto.codigo}</span>
              {proyecto.cliente && (
                <div className="flex items-center gap-1">
                  <Building2 className="h-4 w-4" />
                  {proyecto.cliente.nombre}
                </div>
              )}
              {proyecto.responsable && (
                <div className="flex items-center gap-1">
                  <User className="h-4 w-4" />
                  {proyecto.responsable.nombre}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={proyecto.estado}
            onValueChange={(value) => handleEstadoChange(value as ProjectStatus)}
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(PROJECT_STATUS_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="secondary" onClick={() => setEditModalOpen(true)}>
            <Edit2 className="h-4 w-4 mr-2" />
            Editar
          </Button>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Clock className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Horas</p>
              <p className="text-lg font-semibold">
                {proyecto.horas_registradas}
                {proyecto.horas_estimadas && (
                  <span className="text-gray-400 font-normal"> / {proyecto.horas_estimadas}</span>
                )}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Layers className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Fases</p>
              <p className="text-lg font-semibold">{proyecto.fases_count}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <GitBranch className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Sprints</p>
              <p className="text-lg font-semibold">{proyecto.sprints_count}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Users className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Miembros</p>
              <p className="text-lg font-semibold">{proyecto.miembros_count}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Progress */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Progreso general</span>
          <span className="text-sm font-semibold">{proyecto.progreso_porcentaje.toFixed(0)}%</span>
        </div>
        <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-500 transition-all"
            style={{ width: `${proyecto.progreso_porcentaje}%` }}
          />
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-500">
          {proyecto.fecha_inicio_planeada && (
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              Inicio: {format(new Date(proyecto.fecha_inicio_planeada), 'dd MMM yyyy', { locale: es })}
            </div>
          )}
          {proyecto.fecha_fin_planeada && (
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              Fin: {format(new Date(proyecto.fecha_fin_planeada), 'dd MMM yyyy', { locale: es })}
            </div>
          )}
        </div>
      </Card>

      {/* Descripción */}
      {proyecto.descripcion && (
        <Card className="p-4">
          <h3 className="font-medium text-gray-700 mb-2">Descripción</h3>
          <p className="text-gray-600 whitespace-pre-wrap">{proyecto.descripcion}</p>
        </Card>
      )}

      {/* Tabs: Fases, Sprints, Miembros */}
      <Tabs defaultValue="fases">
        <TabsList>
          <TabsTrigger value="fases">
            <Layers className="h-4 w-4 mr-2" />
            Fases ({proyecto.fases_count})
          </TabsTrigger>
          <TabsTrigger value="sprints">
            <GitBranch className="h-4 w-4 mr-2" />
            Sprints ({proyecto.sprints_count})
          </TabsTrigger>
          <TabsTrigger value="miembros">
            <Users className="h-4 w-4 mr-2" />
            Miembros ({proyecto.miembros_count})
          </TabsTrigger>
          <TabsTrigger value="resumen">
            Resumen
          </TabsTrigger>
        </TabsList>

        <TabsContent value="fases">
          <Card>
            <FasesList proyectoId={proyecto.id} />
          </Card>
        </TabsContent>

        <TabsContent value="sprints">
          <Card>
            <SprintsList proyectoId={proyecto.id} />
          </Card>
        </TabsContent>

        <TabsContent value="miembros">
          <Card>
            <MiembrosList proyectoId={proyecto.id} />
          </Card>
        </TabsContent>

        <TabsContent value="resumen">
          <ProyectoResumenCard proyectoId={proyecto.id} />
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <ProyectoFormModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        proyecto={proyecto}
      />
    </div>
  );
}
